/**
 * categories.js — 3-layer category taxonomy for merchant-level categorization
 *
 * Layers:
 *   Bucket (need/want/savings/income) — hardcoded 50/30/20 logic
 *     └── Group — section headers in flat picker (never navigation gates)
 *           └── Category (~25 core labels, user picks these)
 */

export const CATEGORY_TAXONOMY = [
  { group: 'Income', categories: [
    { label: 'Paycheck',             bucket: 'income' },
    { label: 'Interest & Dividends', bucket: 'income' },
    { label: 'Other Income',         bucket: 'income' },
  ]},
  { group: 'Housing', categories: [
    { label: 'Mortgage / Rent',  bucket: 'need' },
    { label: 'Home Services',    bucket: 'need' },
    { label: 'Home Improvement', bucket: 'want' },
  ]},
  { group: 'Food & Dining', categories: [
    { label: 'Groceries',          bucket: 'need' },
    { label: 'Restaurants & Bars', bucket: 'want' },
    { label: 'Coffee Shops',       bucket: 'want' },
  ]},
  { group: 'Auto & Transport', categories: [
    { label: 'Gas',             bucket: 'need' },
    { label: 'Auto Payment',    bucket: 'need' },
    { label: 'Parking & Tolls', bucket: 'want' },
    { label: 'Ride Shares',     bucket: 'want' },
  ]},
  { group: 'Bills & Utilities', categories: [
    { label: 'Utilities',        bucket: 'need' },
    { label: 'Phone & Internet', bucket: 'need' },
    { label: 'Insurance',        bucket: 'need' },
    { label: 'Subscriptions',    bucket: 'want' },
  ]},
  { group: 'Health & Wellness', categories: [
    { label: 'Medical & Dental', bucket: 'need' },
    { label: 'Fitness',          bucket: 'want' },
  ]},
  { group: 'Shopping & Lifestyle', categories: [
    { label: 'General Shopping', bucket: 'want' },
    { label: 'Clothing',         bucket: 'want' },
    { label: 'Electronics',      bucket: 'want' },
    { label: 'Entertainment',    bucket: 'want' },
    { label: 'Travel',           bucket: 'want' },
    { label: 'Pets',             bucket: 'want' },
    { label: 'Gifts & Charity',  bucket: 'want' },
    { label: 'Kids & Allowance', bucket: 'want' },
  ]},
  { group: 'Financial', categories: [
    { label: 'Debt Payment',     bucket: 'need' },
    { label: 'Savings Transfer', bucket: 'savings' },
    { label: 'Transfers',        bucket: 'savings' },
    { label: 'Fees & Interest',  bucket: 'want' },
  ]},
];

/**
 * Flat O(1) lookup: label.toLowerCase() → bucket
 */
export const CATEGORY_BUCKET_MAP = Object.fromEntries(
  CATEGORY_TAXONOMY.flatMap(g => g.categories.map(c => [c.label.toLowerCase(), c.bucket]))
);

/**
 * Strips store numbers, city/state, legal suffixes, and noise from a raw
 * transaction description to produce a canonical merchant key.
 *
 * Examples:
 *   "DAIRY QUEEN #13805 ANDREWS TX" → "DAIRY QUEEN"
 *   "AMAZON.COM*AB1CD2 SEATTLE WA"  → "AMAZON.COM"
 *   "MCDONALD'S F12345"             → "MCDONALD'S"
 */
export function normalizeMerchant(description) {
  if (!description) return 'UNKNOWN';
  let s = description.toUpperCase();
  s = s.replace(/\*[A-Z0-9]{4,}/g, '');             // AMAZON.COM*AB1CD2 → AMAZON.COM
  s = s.replace(/#\s*\d+\b/g, '');                  // DAIRY QUEEN #13805 → DAIRY QUEEN
  s = s.replace(/\s+[A-Z]{2,}(?:\s+[A-Z]{2})?$/, ''); // trailing city/state
  s = s.replace(/\b(INC|LLC|CO|CORP|LTD)\b\.?/g, '');
  s = s.replace(/\b\d{4,}\b/g, '');
  s = s.replace(/[*#\-_]+/g, ' ');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s.length >= 3 ? s : description.slice(0, 40).toUpperCase().trim();
}

/**
 * Maps pipeline/Teller category strings → taxonomy labels.
 * Returns null if no match — caller should prompt user to pick.
 */
const PIPELINE_TO_TAXONOMY = {
  'groceries':                 'Groceries',
  'food and drink':            'Restaurants & Bars',
  'food_and_drink':            'Restaurants & Bars',
  'dining':                    'Restaurants & Bars',
  'bar':                       'Restaurants & Bars',
  'gas':                       'Gas',
  'fuel':                      'Gas',
  'transport':                 'Auto Payment',
  'utilities':                 'Utilities',
  'phone':                     'Phone & Internet',
  'internet & cable':          'Phone & Internet',
  'insurance':                 'Insurance',
  'medical':                   'Medical & Dental',
  'health':                    'Medical & Dental',
  'health & wellness':         'Medical & Dental',
  'fitness':                   'Fitness',
  'sport':                     'Fitness',
  'entertainment':             'Entertainment',
  'entertainment & recreation':'Entertainment',
  'subscriptions':             'Subscriptions',
  'software':                  'Subscriptions',
  'shopping':                  'General Shopping',
  'general':                   'General Shopping',
  'clothing':                  'Clothing',
  'electronics':               'Electronics',
  'travel':                    'Travel',
  'travel & vacation':         'Travel',
  'accommodation':             'Travel',
  'pets':                      'Pets',
  'gifts':                     'Gifts & Charity',
  'charity':                   'Gifts & Charity',
  'parking & tolls':           'Parking & Tolls',
  'mortgage':                  'Mortgage / Rent',
  'home payment':              'Mortgage / Rent',
  'home':                      'Home Services',
  'home improvement':          'Home Improvement',
  'loan':                      'Debt Payment',
  'student loans':             'Debt Payment',
  'investment':                'Savings Transfer',
  'income':                    'Paycheck',
};

export function suggestCategory(pipelineCategory) {
  if (!pipelineCategory) return null;
  return PIPELINE_TO_TAXONOMY[pipelineCategory.toLowerCase()] || null;
}

/**
 * Guesses a category from the normalized merchant name when source_category is absent.
 * Pattern-matched against common merchant names.
 */
const MERCHANT_NAME_PATTERNS = [
  [/PAYPAL|VENMO|ZELLE|CASH\s*APP|APPLE\s*CASH/i,        'Transfers'],
  [/GREENLIGHT|GOHENRY|FAMZOO|ROOSTER\s*MONEY|ALLOWANCE/i, 'Kids & Allowance'],
  [/WAL\s*MART|WALMART|SAM'?S\s*CLUB|COSTCO|TARGET|DOLLAR\s*(GENERAL|TREE)|FIVE\s*BELOW/i, 'General Shopping'],
  [/H-?E-?B|KROGER|SPROUTS|WHOLE\s*FOODS|TRADER\s*JOE|ALDI|PUBLIX|SAFEWAY|MARKET|GROCERY/i, 'Groceries'],
  [/LOWE'?S|HOME\s*DEPOT|MENARDS|ACE\s*HARDWARE/i,        'Home Improvement'],
  [/MCDONALD|BURGER\s*KING|WENDY'?S|TACO\s*BELL|SONIC|SUBWAY|CHICK-?FIL|DAIRY\s*QUEEN|WHATABURGER|PIZZA|DOMINO|STARBUCKS|DUNKIN|PANDA|CHIPOTLE|POPEYES|KFC/i, 'Restaurants & Bars'],
  [/RESTAURANT|GRILL|BISTRO|CAFE|DINER|EATERY|STEAKHOUSE|SUSHI|TAQUER/i, 'Restaurants & Bars'],
  [/TEANERGY|COFFEE|ESPRESSO|BOBA/i,                       'Coffee Shops'],
  [/AMAZON|EBAY|ETSY|WISH|WAYFAIR|CHEWY|OVERSTOCK/i,      'General Shopping'],
  [/SHELL|EXXON|CHEVRON|VALERO|MURPHY|LOVE'?S|PILOT|KENT\s*KWIK|STRIPES|SUNOCO|BP\b|TEXACO/i, 'Gas'],
  [/NETFLIX|HULU|SPOTIFY|DISNEY|APPLE\.COM|GOOGLE\s*PLAY|AMAZON\s*PRIME|HBO|PARAMOUNT|PEACOCK/i, 'Subscriptions'],
  [/AT&T|VERIZON|T-?MOBILE|SPRINT|COMCAST|XFINITY|SPECTRUM|DIRECTV|DISH\b/i, 'Phone & Internet'],
  [/INSURANCE|GEICO|STATE\s*FARM|ALLSTATE|PROGRESSIVE|USAA|NATIONWIDE/i, 'Insurance'],
  [/DOCTOR|DENTAL|CLINIC|HOSPITAL|PHARMACY|PRESCRIPTION|CVS|WALGREEN|RITE\s*AID|NEUROLOGY|DERMATOLOGY/i, 'Medical & Dental'],
  [/GYM|FITNESS|PLANET\s*FITNESS|CROSSFIT|YMCA|ANYTIME\s*FITNESS/i, 'Fitness'],
  [/UBER|LYFT|DOORDASH|GRUBHUB|INSTACART/i,               'Ride Shares'],
  [/ELECTRIC|GAS\s*(COMPANY|SERVICE|BILL)|WATER\s*(COMPANY|BILL)|ATMOS|ONCOR|AEP\b/i, 'Utilities'],
  [/MORTGAGE|LOAN\s*PAYMENT|PENNYMAC|QUICKEN|ROCKET\s*MORTGAGE/i, 'Mortgage / Rent'],
];

export function suggestFromMerchantName(merchantKey) {
  if (!merchantKey) return null;
  for (const [pattern, label] of MERCHANT_NAME_PATTERNS) {
    if (pattern.test(merchantKey)) return label;
  }
  return null;
}
